import { callAvosApi } from "../../utils/useAvosApiHook";

/**
 * Fetches the full blob URL with SAS token from the API given a blob name.
 * Used for tenant config images, defect images, and other blob storage assets.
 */
export const fetchBlobUrl = async (blob_name: string): Promise<string> => {
    const { data } = await callAvosApi(`/images/blob_url?blob_name=${encodeURIComponent(blob_name)}`);
    return data;
};

export const fetchBlobFromUrl = async (url: string): Promise<Blob> => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    // Verify we got an actual image, not an XML/text error payload.
    if (blob.type.includes("xml") || blob.type.includes("text")) {
        throw new Error(`Expected image but received ${blob.type}`);
    }

    return blob;
};

/* =======================
 * Constants
 * ======================= */

export const MAX_IMAGE_WIDTH = 1600;
export const MAX_IMAGE_HEIGHT = 1200;
export const MAX_IMAGE_AREA = MAX_IMAGE_WIDTH * MAX_IMAGE_HEIGHT;

/* =======================
 * Low-level helpers
 * ======================= */

/**
 * Resize any CanvasImageSource if it exceeds MAX_IMAGE_AREA.
 * Returns a canvas (always).
 */
export const resizeCanvasIfNeeded = (
    source: CanvasImageSource,
    width: number,
    height: number
): HTMLCanvasElement => {
    const imageArea = width * height;

    const scale = imageArea > MAX_IMAGE_AREA
        ? Math.sqrt(MAX_IMAGE_AREA / imageArea)
        : 1;

    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext("2d")!;
    // Fill with white so transparent areas don't turn black when encoding to JPEG
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

    return canvas;
};

/**
 * Load an image element from a base64 string.
 * (Used by legacy base64 callers)
 */
export const createImageFromBase64 = (base64: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = base64;
});

/* =======================
 * Public API (existing usage)
 * ======================= */

/**
 * Existing API — kept for compatibility.
 * Shrinks a base64 image if it exceeds MAX_IMAGE_AREA.
 *
 * NOTE:
 * - Still uses base64 internally (for legacy callers)
 * - Internally uses the shared canvas resize logic
 */
export const checkImageSizeAndShrinkIfNeeded = async (
    base64: string,
    width: number,
    height: number
): Promise<string> => {
    const imageArea = width * height;
    if (imageArea <= MAX_IMAGE_AREA) {
        return base64;
    }

    const img = await createImageFromBase64(base64);
    const canvas = resizeCanvasIfNeeded(img, width, height);

    // Preserve original behavior: output webp
    return canvas.toDataURL("image/webp");
};

/* =======================
 * PDF / WebP → JPEG path
 * ======================= */

/**
 * Convert a WebP image URL to a JPEG base64 string.
 * Optimized path:
 * - single decode
 * - single canvas draw
 * - single encode
 */
export async function webpToJpgBase64(url: string): Promise<string> {
    if (!url) return "";

    try {
        const response = await fetch(url);
        const blob = await response.blob();

        // Fast decode (where supported)
        const bitmap = await createImageBitmap(blob);

        const canvas = resizeCanvasIfNeeded(
            bitmap,
            bitmap.width,
            bitmap.height
        );

        // JPEG tuned for PDFs
        return canvas.toDataURL("image/jpeg", 0.8);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error converting webp to jpg", error);
        return "";
    }
}

export default webpToJpgBase64;

export const equalizeHistogram = (imageData, sensitivity = 5) => {
    // Ensure sensitivity is within the valid range
    sensitivity = Math.max(1, Math.min(9, sensitivity));

    const data = imageData.data; // eslint-disable-line prefer-destructuring
    const histogram = new Array(256).fill(0);
    let mean = 0;
    let variance = 0;

    // Step 1: Calculate histogram and mean grayscale value
    for (let i = 0; i < data.length; i += 4) {
        const gray = data[i]; // Assuming image is already grayscale
        histogram[gray]++;
        mean += gray;
    }

    const totalPixels = imageData.width * imageData.height;
    mean /= totalPixels;

    // Step 2: Calculate variance (standard deviation squared)
    for (let i = 0; i < 256; i++) {
        variance += ((i - mean) ** 2) * histogram[i];
    }
    variance /= totalPixels;
    const stdDev = Math.sqrt(variance);

    // Step 3: Decide whether to perform equalization
    let performEqualization = false;

    if (sensitivity === 9) {
        // Always perform equalization
        performEqualization = true;
    } else if (sensitivity === 1) {
        // Skip equalization
        performEqualization = false;
    } else {
        // Calculate threshold based on sensitivity
        const maxStdDev = 74; // Approximate maximum standard deviation for an 8-bit grayscale image
        const threshold = ((10 - sensitivity) / 9) * maxStdDev;

        // If the standard deviation is less than the threshold, perform equalization
        if (stdDev < threshold) {
            performEqualization = true;
        }
    }

    if (performEqualization) {
        // Step 4: Calculate cumulative histogram
        const cumulativeHistogram = new Array(256).fill(0);
        cumulativeHistogram[0] = histogram[0]; // eslint-disable-line prefer-destructuring
        for (let i = 1; i < 256; i++) {
            cumulativeHistogram[i] = cumulativeHistogram[i - 1] + histogram[i];
        }

        // Step 5: Normalize cumulative histogram
        const scaleFactor = 255 / totalPixels;
        const lookupTable = cumulativeHistogram.map((count) => Math.round(count * scaleFactor));

        // Step 6: Apply equalization to each pixel
        for (let i = 0; i < data.length; i += 4) {
            const gray = data[i];
            const equalizedValue = lookupTable[gray];

            // Assign the equalized grayscale value to each color channel
            data[i] = equalizedValue;
            data[i + 1] = equalizedValue;
            data[i + 2] = equalizedValue;
            // Alpha channel remains unchanged
        }
    }

    return imageData;
};
